import Layout from "@/components/layout/Layout";
import SEO from "@/components/SEO";
import { FileText, Download, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import sellSheetFull from "@/assets/sell-sheet-full.jpg";
import cutSheetFull from "@/assets/cut-sheet-full.jpg";

const documents = [
  {
    title: "Product Sell Sheet",
    description: "Complete Pronto product specifications, styles, sizes, and color availability overview.",
    href: "/docs/Pronto-Sell-Sheet.pdf",
    preview: sellSheetFull,
  },
  {
    title: "Technical Cut Sheet",
    description: "Engineering drawing with material specs, thickness, and construction details.",
    href: "/docs/Gemini-Pronto-Cut-Sheet.pdf",
    preview: cutSheetFull,
  },
];

const guides = [
  {
    title: "Pronto Track Installation Guide",
    description: "Step-by-step instructions for mounting Pronto Track to your sign panel.",
  },
  {
    title: "Change Arm Usage Guide",
    description: "How to use the telescoping change arm to swap letters safely from ground level.",
  },
  {
    title: "Letter Sizing & Spacing Reference",
    description: "Recommended letter heights, spacing, and readability distances for each size.",
  },
];

export default function Resources() {
  return (
    <Layout>
      <SEO
        title="Resources — Product Sheets & Installation Guides"
        description="Download Gemini Pronto™ product sell sheets, technical cut sheets, and step-by-step installation guides for Pronto Track and change arms."
        canonical="/resources"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Pronto Letters Resources",
          description: "Product sell sheets, technical cut sheets, and installation guides for Gemini Pronto™ changeable letters.",
          url: "https://pronto-letters.com/resources",
        }}
      />
      <div className="container max-w-4xl py-16">
        <h1 className="font-display text-4xl font-bold">Resources</h1>
        <p className="mt-2 text-muted-foreground">Product sheets, installation guides, and reference materials.</p>

        {/* Downloadable documents with previews */}
        <h2 className="mt-10 font-display text-2xl font-semibold">Product Documents</h2>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {documents.map((doc) => (
            <Card key={doc.title} className="group overflow-hidden transition-shadow hover:shadow-lg">
              <Dialog>
                <DialogTrigger asChild>
                  <button className="w-full overflow-hidden">
                    <img
                      src={doc.preview}
                      alt={doc.title}
                      className="h-56 w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl">
                  <img src={doc.preview} alt={doc.title} className="w-full" />
                </DialogContent>
              </Dialog>
              <CardContent className="p-5">
                <h3 className="font-display font-semibold">{doc.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{doc.description}</p>
                <a
                  href={doc.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-accent hover:underline"
                >
                  <Download className="h-4 w-4" /> Download PDF
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Guides */}
        <h2 className="mt-14 font-display text-2xl font-semibold">Installation Guides</h2>
        <div className="mt-6 space-y-4">
          {guides.map((g) => (
            <Card key={g.title} className="transition-shadow hover:shadow-md">
              <CardContent className="flex items-start gap-4 p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-accent/10">
                  <FileText className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold">{g.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{g.description}</p>
                  <p className="mt-2 text-xs text-muted-foreground italic">PDF download coming soon</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
}
